import { useEffect, useState, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { useRouteMatch } from "react-router-dom";
import Offer from "../components/offer";
import SwiperCore, { Autoplay, EffectFade, Navigation, Pagination } from "swiper";
import axios from "axios";
import CarUser from "./components/CarUser";
import config from "../configuration/config";
import { groupBy, mapValues, omit } from "lodash";
import { useTranslation } from "react-i18next";
import { getData } from "../utils/localStorageManager";
import { Link } from "react-router-dom";
import useUserCars from "./hooks/useUserCars";
import { slugCombine } from "../utils/slugManager";
import "./UserV7.scss";

SwiperCore.use([Pagination, Navigation, Autoplay, EffectFade]);

export default function UserHomePage() {
    const { t: trans, i18n } = useTranslation(["global", "model", "common", "user"]);
    const { url } = useRouteMatch();
    const languageDir = 'language-' + (i18n.language || 'ar');
    const [offers, setOffers] = useState([]);
    const [notificationCount, setNotificationCount] = useState(0);

    const { data: cars = [] } = useUserCars();

    const fetchNotifications = useCallback(async () => {
        try {
            const userKey = getData('secretKey');
            if (!userKey) return;
            const response = await axios.post(`${config.controllURL}${i18n.language || 'ar'}/api/Pages/SiteusernotificationsFunc`, { id: userKey });
            if (response.data) setNotificationCount(response.data.length);
        } catch (e) { console.log(e); }
    }, []);

    const initialUserOffers = useCallback(() => {
        try {
            const isLogin = getData("isAuthenticated");
            if (isLogin) {
                const userID = getData('secretKey');
                axios.post(`${config.controllURL}${i18n.language || 'ar'}/api/Pages/SiteFetchActiveOffers`, { Id: userID }).then(response => {
                    const result = response.data;
                    const groupResult = mapValues(groupBy(result, 'OfferType'), fList => fList.map(items => omit(items, 'OfferType')));
                    let finalResults = [];
                    for (let item in groupResult) {
                        const obj = { heading: '', items: [] };
                        const results = groupResult[item].map(it => it[`Type${trans("model:description")}`]);
                        if (results?.length > 0) obj.heading = results[0];
                        obj.items = groupResult[item];
                        finalResults.push(obj);
                    }
                    setOffers(finalResults);
                }).catch(e => console.log(e));
            }
        } catch (e) { console.log(e); }
    }, [trans]);

    useEffect(() => {
        initialUserOffers();
        fetchNotifications();
    }, [initialUserOffers, fetchNotifications]);

    return (
        <div className={`user-dashboard-v7 ${languageDir}`}>
            {/* Statistics Grid */}
            <div className="stats-grid-v7">
                <Link to={`${url}/vehicles`} className="stat-card-v7 no-underline">
                    <div className="stat-icon-v7 green">
                        <img src="/assets/user_profile/icons/car.svg" alt="" />
                    </div>
                    <div className="stat-info-v7">
                        <span className="stat-value-v7">{cars?.length || 0}</span>
                        <span className="stat-label-v7">{trans("common:vehicle")}</span>
                    </div>
                </Link>
                <Link to={`${url}/notifications`} className="stat-card-v7 no-underline">
                    <div className="stat-icon-v7 orange">
                        <img src="/assets/user_profile/icons/notification.svg" alt="" />
                    </div>
                    <div className="stat-info-v7">
                        <span className="stat-value-v7">{notificationCount}</span>
                        <span className="stat-label-v7">{trans("user:notifications")}</span>
                    </div>
                </Link>
                <Link to={`${url}/offers`} className="stat-card-v7 no-underline">
                    <div className="stat-icon-v7 blue">
                        <img src="/assets/user_profile/icons/offer.svg" alt="" />
                    </div>
                    <div className="stat-info-v7">
                        <span className="stat-value-v7">{offers?.reduce((acc, curr) => acc + curr.items.length, 0)}</span>
                        <span className="stat-label-v7">{trans("home:offers")}</span>
                    </div>
                </Link>
            </div>

            {/* My Cars Section */}
            <div className="dashboard-section-v7">
                <div className="section-header-v7">
                    <h2 className="title-v7">{trans("common:my-cars")}</h2>
                    <Link to="/user/vehicles" className="view-all-v7">{trans("common:view-all")}</Link>
                </div>
                <div className="cars-grid-v7">
                    {cars?.length > 0 ? (
                        <Swiper
                            spaceBetween={20}
                            slidesPerView="auto"
                            className="cars-swiper-v7"
                            observer={true}
                            observeParents={true}
                            loop={false}
                        >
                            {cars.slice(0, 10).map((car, idx) => (
                                <SwiperSlide key={idx} style={{ width: '280px' }}>
                                    <CarUser
                                        id={car.CarID}
                                        img={car.Image || '/assets/images/parts/default.png'}
                                        name={car[`Model${trans('model:description')}`] ?? car[`Group${trans('model:description')}`]}
                                        brand={car[`Brand${trans('model:description')}`]}
                                        userCarName={car?.alias}
                                        link={"/user/vehicles/privateVehicles/" + car.CarID + "/" + car.FetchType}
                                        showDetails={false}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    ) : (
                        <div className="empty-state-v7">
                            <img src="/assets/images/no-data.svg" alt="" />
                            <p>{trans("common:no-data")}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Active Offers Section */}
            {offers.map((section, idx) => (
                <div key={idx} className="dashboard-section-v7">
                    <div className="section-header-v7">
                        <h2 className="title-v7">{section.heading}</h2>
                        {section.items.length > 4 && (
                            <Link className="view-all-v7" to={`/offers/type/${section.heading}`}>
                                {trans("common:show-more")}
                            </Link>
                        )}
                    </div>
                    <div className="offers-swiper-container-v7">
                        <Swiper
                            spaceBetween={20}
                            slidesPerView="auto"
                            className="offers-swiper-v7"
                            observer={true}
                            observeParents={true}
                            watchSlidesProgress={true}
                            loop={false}
                        >
                            {section.items.map((item, i) => (
                                <SwiperSlide key={i} style={{ width: '320px' }}>
                                    <Offer
                                        img={item.Image || item.Logo}
                                        name={item[`Title${trans("model:description")}`]}
                                        excerpt={item[`Sub${trans("model:description")}`]}
                                        type={item.OfferType}
                                        slug={slugCombine(item.slugAr, item.slugEn)}
                                        price={item?.price || item?.Price}
                                        endDate={item.EndDate}
                                        startDate={item.StartDate}
                                        totalDays={item.TotalDays}
                                        showSeconds={false}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </div>
            ))}
        </div>
    );
}